/**
 * Portal logins - your customers' own people.
 *
 * A SEPARATE PAGE FROM TEAM MEMBERS, not a tab on it, and the separation is the
 * point. Adding a technician and handing a customer a login are different
 * powers with different consequences, gated on different capabilities
 * (members.manage vs clients.portal). Putting them on one screen invites the
 * exact slip the split exists to prevent: choosing the wrong list from a
 * dropdown and giving a customer's address a staff role, or the reverse.
 *
 * WHAT A PORTAL LOGIN ACTUALLY GRANTS is said on the page rather than left to
 * be inferred, because the person typing an address is making a promise to a
 * customer and needs to know exactly which one. It is one document: machine
 * names, whether each last backup succeeded, and thirty days of history. No
 * reason strings, no vendor names, no other customer - see functions/portal.ts.
 */

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

interface ClientUser {
  uid: string;
  email: string;
  clientId: string;
  clientName: string;
  clientIds?: string[];
  clients?: { id: string; name: string }[];
  invitedBy: string;
  invitedAt: number | null;
  disabled: boolean;
  state: 'active' | 'invited' | 'disabled';
  mfaEnrolled: boolean;
  lastSignIn: string | null;
}

interface Client {
  id: string;
  name: string;
}

/**
 * INVITED is the state a plain list cannot show and the one that matters: the
 * account exists, the customer believes they have a portal, and nobody has ever
 * signed in. Identical to a working login unless it is said out loud.
 */
function StatePill({ u }: { u: ClientUser }) {
  if (u.state === 'disabled') {
    return <span className="pill pill-bad" title="Cannot sign in">Disabled</span>;
  }
  if (u.state === 'invited') {
    return <span className="pill pill-warn" title="Invited, but has never signed in">Invited</span>;
  }
  return (
    <span
      className="pill pill-ok"
      title={u.lastSignIn ? `Last signed in ${new Date(u.lastSignIn).toLocaleString()}` : 'Has signed in'}
    >
      Active
    </span>
  );
}

export function ClientUsers({ orgId, canManage }: { orgId: string | null; canManage: boolean }) {
  const [users, setUsers] = useState<ClientUser[] | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const [email, setEmail] = useState('');
  const [clientId, setClientId] = useState('');
  /** Which row's site list is open, and the ticks in it. */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<string[]>([]);

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      const [u, c] = await Promise.all([
        httpsCallable(functions, 'listClientUsers')({ orgId }),
        httpsCallable(functions, 'listClients')({ orgId }),
      ]);
      setUsers((u.data as { users: ClientUser[] }).users ?? []);
      setClients(((c.data as { clients: Client[] }).clients ?? []).slice());
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'Could not load portal logins.' });
      setUsers([]);
    }
  }, [orgId]);

  useEffect(() => { void load(); }, [load]);

  if (!orgId) return null;

  async function run(key: string, fn: () => Promise<string>) {
    setBusy(key); setMsg(null);
    try {
      setMsg({ ok: true, text: await fn() });
      await load();
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'That did not work.' });
    } finally {
      setBusy(null);
    }
  }

  const invite = () => run('invite', async () => {
    const res = await httpsCallable(functions, 'inviteClientUser')({ orgId, email, clientId });
    const d = res.data as { emailed: boolean; emailError: string | null; created: boolean };
    const who = email;
    setEmail('');
    // "Added, but they were never told" must never read as plain success - the
    // customer is waiting for a mail that is not coming and nobody knows.
    if (!d.emailed) {
      throw new Error(
        `${who} now has a portal login, but the invitation email failed: ${d.emailError ?? 'unknown error'}. ` +
        'Check the Resend integration, then suspend and re-invite them.',
      );
    }
    return `Invited ${who}.`;
  });

  return (
    <section className="panel">
      <div className="panel-title">Portal logins</div>
      <p className="panel-note">
        People at your customers who can sign in and see their own machines. A portal login shows
        machine names, whether the last backup succeeded, and a thirty-day history - and nothing
        else. No error text, no vendor names, and never another customer.
      </p>

      {msg && <div className={'banner ' + (msg.ok ? 'banner-ok' : 'banner-error')}>{msg.text}</div>}

      {canManage && clients.length > 0 && (
        <div className="row-form" style={{ marginBottom: 14 }}>
          <label className="field" style={{ flex: 2 }}>
            <span className="field-label">Invite by email</span>
            <input
              className="field-input" type="email" placeholder="their.name@customer.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="field" style={{ flex: 2 }}>
            <span className="field-label">Client</span>
            <select className="field-input" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Choose a client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <button
            className="btn btn-primary"
            disabled={!email || !clientId || busy === 'invite'}
            title={!clientId ? 'Choose which client this person belongs to' : undefined}
            onClick={() => void invite()}
          >
            {busy === 'invite' ? 'Inviting...' : 'Send portal invite'}
          </button>
        </div>
      )}

      {users !== null && clients.length === 0 && (
        <p className="empty">
          No clients exist yet, so there is nobody to give a portal to. Machines are matched to
          clients under Review.
        </p>
      )}

      {users === null && <p className="empty">Loading...</p>}

      {users !== null && users.length === 0 && clients.length > 0 && (
        <p className="empty">Nobody has a portal login yet.</p>
      )}

      {users !== null && users.length > 0 && (
        <table className="mini-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Client</th>
              <th>State</th>
              <th>Two-factor</th>
              {canManage && <th></th>}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.uid} className={u.disabled ? 'row-disabled' : undefined}>
                <td>{u.email}</td>
                <td>
                  {editing === u.uid ? (
                    /*
                     * THE PICKER. Damon: "id have added the email and then had
                     * a add to picker that allowed you to add any orgs to that
                     * user."
                     *
                     * One decision about a SET, not one invitation per site -
                     * an owner covering nine practices should tick nine boxes
                     * once, not receive nine emails.
                     */
                    <div className="cu-picker">
                      <div className="cu-picker-list">
                        {clients.map((c) => (
                          <label className="cu-picker-row" key={c.id}>
                            <input
                              type="checkbox"
                              checked={draft.includes(c.id)}
                              onChange={(e) =>
                                setDraft((d) =>
                                  e.target.checked ? [...d, c.id] : d.filter((x) => x !== c.id),
                                )
                              }
                            />
                            <span>{c.name}</span>
                          </label>
                        ))}
                      </div>
                      <div className="cu-picker-actions">
                        <button
                          className="btn-link"
                          disabled={busy === u.uid}
                          onClick={() => void run(u.uid, async () => {
                            await httpsCallable(functions, 'setClientUserClients')(
                              { orgId, uid: u.uid, clientIds: draft },
                            );
                            setEditing(null);
                            return draft.length
                              ? `${u.email} now sees ${draft.length} site${draft.length === 1 ? '' : 's'}.`
                              : `Removed the portal login for ${u.email}.`;
                          })}
                        >
                          Save
                        </button>
                        <button className="btn-link" onClick={() => setEditing(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Every site, not the first one. A row that names one of
                          nine is a row that misleads at a glance. */}
                      <div>{(u.clients?.length ? u.clients.map((c) => c.name) : [u.clientName]).join(', ')}</div>
                      {canManage && (
                        <button
                          className="btn-link"
                          style={{ fontSize: 11 }}
                          onClick={() => {
                            setEditing(u.uid);
                            setDraft(u.clientIds?.length ? [...u.clientIds] : [u.clientId].filter(Boolean));
                          }}
                        >
                          {(u.clients?.length ?? 1) > 1 ? 'change sites' : 'add sites'}
                        </button>
                      )}
                    </>
                  )}
                </td>
                <td><StatePill u={u} /></td>
                <td>
                  {u.mfaEnrolled
                    ? <span className="pill pill-ok">On</span>
                    : <span className="dim">Not enrolled</span>}
                </td>
                {canManage && (
                  <td>
                    {/* Suspend is offered first, and delete second, for the same
                        reason as on the team page: suspending keeps the record
                        of who granted the access and when. */}
                    <button
                      className="btn-link"
                      disabled={busy === u.uid}
                      onClick={() => void run(u.uid, async () => {
                        await httpsCallable(functions, 'setClientUserDisabled')(
                          { orgId, uid: u.uid, disabled: !u.disabled },
                        );
                        return u.disabled ? `Restored ${u.email}.` : `Suspended ${u.email}.`;
                      })}
                    >
                      {u.disabled ? 'Restore' : 'Suspend'}
                    </button>
                    <button
                      className="btn-link danger"
                      disabled={busy === u.uid}
                      onClick={() => {
                        if (!confirm(`Remove the portal login for ${u.email}?`)) return;
                        void run(u.uid, async () => {
                          await httpsCallable(functions, 'removeClientUser')({ orgId, uid: u.uid });
                          return `Removed ${u.email}.`;
                        });
                      }}
                    >
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!canManage && (
        <p className="panel-note">
          You can see who has a portal login, but not change it. That needs the
          &ldquo;Invite client users&rdquo; permission.
        </p>
      )}
    </section>
  );
}
